import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/core/styles';
import { setSelectedNode, setNodeById } from '../../../features/mainSlice';
import { color } from '../../../app/theme';
import TextField from '../../shared/TextField';
import Dropdown from '../../shared/Dropdown';
import { KeyWordFilterType } from '../../../app/types';
import isAdd from '../../../assets/images/svg/ic-add-circle-20-hover.svg';
import isMinus from '../../../assets/images/svg/ic-minus-circle.svg';

const useStyles = makeStyles(
  {
    title: {
      fontSize: '15px',
      fontWeight: '500',
      fontStretch: 'normal',
      fontStyle: 'normal',
      lineHeight: '1',
      letterSpacing: 'normal',
      color: color.$3,
      marginBottom: 12,
    },
    row: {
      marginBottom: 12,
    },
    dropdown: {
      marginBottom: 12,
    },
    input: {
      display: 'flex',
      alignItems: 'center',
    },
    add: {
      display: 'flex',
      alignItems: 'center',
      fontSize: '15px',
      fontWeight: 'normal',
      fontStretch: 'normal',
      fontStyle: 'normal',
      lineHeight: '1',
      letterSpacing: 'normal',
      color: color.$2,
      cursor: 'pointer',
    },
    minus: {
      marginLeft: 8,
    },
  },
  {
    name: 'Keywords',
  },
);

const hasAddTypes = [
  KeyWordFilterType.KEYWORD,
  KeyWordFilterType.IN,
  KeyWordFilterType.NIN,
  KeyWordFilterType.STARTSWITH,
  KeyWordFilterType.ENDSWITH,
];

const Keywords = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { selectedNode, isErrorNodeIds } = useSelector(state => state.ma);
  const { data } = selectedNode || {};
  const { conditions } = data || {};
  const condition = (conditions || [])[0] || {};
  const { type, value } = condition || {};
  const isAddTypes = useMemo(() => !!~hasAddTypes.indexOf(type), [type]);
  const isError = !!~isErrorNodeIds.indexOf(selectedNode?.id);

  const keyWordFilterItems = [
    {
      text: t(`trigger-setting.${KeyWordFilterType.KEYWORD}`),
      value: KeyWordFilterType.KEYWORD,
    },
    {
      text: t(`trigger-setting.${KeyWordFilterType.IN}`),
      value: KeyWordFilterType.IN,
    },
    {
      text: t(`trigger-setting.${KeyWordFilterType.NIN}`),
      value: KeyWordFilterType.NIN,
    },
    {
      text: t(`trigger-setting.${KeyWordFilterType.EQ}`),
      value: KeyWordFilterType.EQ,
    },
    {
      text: t(`trigger-setting.${KeyWordFilterType.NE}`),
      value: KeyWordFilterType.NE,
    },
    {
      text: t(`trigger-setting.${KeyWordFilterType.LT}`),
      value: KeyWordFilterType.LT,
    },
    {
      text: t(`trigger-setting.${KeyWordFilterType.LTE}`),
      value: KeyWordFilterType.LTE,
    },
    {
      text: t(`trigger-setting.${KeyWordFilterType.GT}`),
      value: KeyWordFilterType.GT,
    },
    {
      text: t(`trigger-setting.${KeyWordFilterType.GTE}`),
      value: KeyWordFilterType.GTE,
    },
    {
      text: t(`trigger-setting.${KeyWordFilterType.STARTSWITH}`),
      value: KeyWordFilterType.STARTSWITH,
    },
    {
      text: t(`trigger-setting.${KeyWordFilterType.ENDSWITH}`),
      value: KeyWordFilterType.ENDSWITH,
    },
  ];

  const onChangeTagFilterType = v => {
    const isAddTypesByNewType = !!~hasAddTypes.indexOf(v);

    const newNode = {
      ...selectedNode,
      data: {
        ...data,
        conditions: [
          {
            type: v,
            value: isAddTypesByNewType ? [''] : 0,
          },
        ],
      },
    };

    dispatch(setSelectedNode(newNode));
    dispatch(setNodeById(newNode));
  };

  const onChangeInt = event => {
    const { value: v } = event.target;

    const newNode = {
      ...selectedNode,
      data: {
        ...data,
        conditions: [
          {
            ...condition,
            value: parseInt(v, 10) || 0,
          },
        ],
      },
    };

    dispatch(setSelectedNode(newNode));
    dispatch(setNodeById(newNode));
  };

  const onChangeText = (event, index) => {
    const { value: v } = event.target;

    const newNode = {
      ...selectedNode,
      data: {
        ...data,
        conditions: [
          {
            ...condition,
            value: (condition?.value || []).map((o, i) => {
              if (i === index) return v;
              return o;
            }),
          },
        ],
      },
    };

    dispatch(setSelectedNode(newNode));
    dispatch(setNodeById(newNode));
  };

  const onAdd = () => {
    const newNode = {
      ...selectedNode,
      data: {
        ...data,
        conditions: [
          {
            ...condition,
            value: [...(condition?.value || []), ''],
          },
        ],
      },
    };

    dispatch(setSelectedNode(newNode));
    dispatch(setNodeById(newNode));
  };

  const onMinus = index => {
    const newNode = {
      ...selectedNode,
      data: {
        ...data,
        conditions: [
          {
            ...condition,
            value: (condition?.value || []).filter((c, i) => i !== index),
          },
        ],
      },
    };

    dispatch(setSelectedNode(newNode));
    dispatch(setNodeById(newNode));
  };

  const conditionsComponents = useMemo(() => {
    if (isAddTypes) {
      return (value || []).map((v, index) => {
        return (
          <div
            className={classes.row}
            key={`conditions-components-${selectedNode?.id}-${index}`}
          >
            <div className={classes.input}>
              <TextField
                type="text"
                isValid
                placeholder={t('trigger-setting.keywords-placeholder')}
                value={v}
                onChange={event => onChangeText(event, index)}
                isError={isError && !v}
              />
              {(value || []).length > 1 && (
                <img
                  className={classes.minus}
                  onClick={() => onMinus(index)}
                  src={isMinus}
                  alt="minus"
                />
              )}
            </div>
          </div>
        );
      });
    }
    return (
      <div className={classes.row}>
        <div className={classes.input}>
          <TextField
            type="text"
            isValid
            placeholder={t('trigger-setting.keywords-placeholder')}
            value={value || 0}
            onChange={event => onChangeInt(event)}
          />
        </div>
      </div>
    );
  }, [type, value, isError]);

  return (
    <div>
      <div className={classes.title}>{t('trigger-setting.keywords')}</div>
      <Dropdown
        className={classes.dropdown}
        menuItems={keyWordFilterItems}
        value={type}
        onChange={v => onChangeTagFilterType(v)}
      />
      {conditionsComponents}
      {isAddTypes && (
        <div className={classes.add} onClick={onAdd}>
          <img src={isAdd} alt="add" />
          {t('trigger-setting.add-keywords')}
        </div>
      )}
    </div>
  );
};

export default Keywords;
